import { useMemo } from "react";

import BlobIDV from "assets/icons/BlobIDV/BlobIDV.svg";
import BlobPadlock from "assets/icons/BlobPadlock/BlobPadlock.svg";
import BlobTime from "assets/icons/BlobTime/BlobTime.svg";
import AddBeneficiariesIntroImg from "assets/images/illustrations/auto_deposit_intro.svg";
import HandsOnUmbrella from "assets/images/illustrations/hands-on-umbrella.svg";
import ResumeApplicationImg from "assets/images/illustrations/resume-application/resume-application.svg";
import SurfingWithDolphinsImg from "assets/images/illustrations/surfing-with-dolphins.svg";
import UnderwritingIntroImage from "assets/images/illustrations/underwriting-hero.svg";
import { CustomerSupportContent } from "components/CustomerSupportContent/CustomerSupportContent";
import Divider from "components/Divider/Divider";
import FormDecorator from "components/FormDecorator/FormDecorator";
import FormValueProps from "components/FormValueProps/FormValueProps";
import Icon from "components/Icon/Icon";
import IneligibleWarningIFrame from "components/IneligibleWarning/IneligibleWarningIFrame";
import Text from "components/Text/Text";
import { TransitionImage } from "components/TransitionImage";
import UnderwritingSteps from "components/UnderwritingSteps/UnderwritingSteps";
import VideoPlayer from "components/VideoPlayer/VideoPlayer";
import { DecorationFormItem, ManualUnderwritingURL } from "config";
import { useTranslation } from "modules";

export const useDecorationItemBySlug = (slug: DecorationFormItem["content"]["slug"]) => {
  const { t } = useTranslation();

  const getDecoratorBySlug = useMemo(() => {
    switch (slug) {
      case "age-ineligible-iframe--media-alpha": {
        return <IneligibleWarningIFrame ineligibilityReason="age_of_insured" />;
      }

      case "bene-delete-confirm-image": {
        return <Icon size="xl" src="BlobAlert" />;
      }

      case "bene-setup-image": {
        return (
          <div className="dynamic-form-item__decoration--illustration">
            <img
              className="dynamic-form-item__decoration--image"
              alt="Piggy bank allocating coins"
              src={AddBeneficiariesIntroImg}
            />
          </div>
        );
      }

      case "beneficiary-delete-confirm-image": {
        return <Icon size="xl" src="BlobAlert" />;
      }

      case "beneficiary-promote-image": {
        return <Icon size="xl" src="BlobAlert" />;
      }

      case "customer-support-content": {
        const props = {
          className: "dynamic-form-item__decoration--customer-support",
          dataTestId: "dynamic-form-decorator__customer-support-content",
        };

        return <CustomerSupportContent {...props} />;
      }

      case "referral-partner-screen-image--default": {
        return (
          <img
            className="dynamic-form-item__decoration--image"
            alt="Hands holding an umbrella"
            src={HandsOnUmbrella}
          />
        );
      }

      case "flower-icon": {
        return <Icon size="xl" src="BlobSorry" />;
      }

      case "location-ineligible-iframe--media-alpha": {
        return <IneligibleWarningIFrame ineligibilityReason="state_of_residence" />;
      }

      case "location-ineligible-image--california": {
        return (
          <img
            className="dynamic-form-item__decoration--image"
            alt="A man and woman surfing with dolphins"
            src={SurfingWithDolphinsImg}
          />
        );
      }

      case "lightbulb-icon": {
        return <Icon size="xl" src="BlobLightbulb" />;
      }

      case "mail-icon": {
        return <Icon size="xl" src="BlobMail" />;
      }

      case "manual-underwriting-video": {
        return (
          <div className="dynamic-form-item__decoration--video" style={{ height: 240 }}>
            <VideoPlayer
              url={ManualUnderwritingURL}
              options={{
                height: 240,
                config: { vimeo: { title: true } },
              }}
            />
          </div>
        );
      }

      case "privacy-insurance-application": {
        const adCardContent = [
          {
            title: t("form.ssn.info.1.title", "YOUR DATA IS PROTECTED"),
            description: t(
              "form.ssn.info.1.description",
              "We use industry-best encryption and security technology to keep your personal information safe."
            ),
            imgSrc: BlobPadlock,
          },
          {
            title: t("form.ssn.info.3.title", "VERIFIED BY A TRUSTED PARTNER"),
            description: t(
              "form.ssn.info.3.description",
              "Our third-party verification service lets us accurately confirm your identity and personal information."
            ),
            imgSrc: BlobIDV,
          },
        ];

        return <FormValueProps content={adCardContent} />;
      }

      case "privacy-insurance-application-vertical": {
        const adCardContent = [
          {
            title: t("form.ssn.info.3.title", "VERIFIED BY A TRUSTED PARTNER"),
            description: t(
              "form.ssn.info.3.description",
              "Our third-party verification service lets us accurately confirm your identity and personal information."
            ),
            imgSrc: BlobIDV,
          },
          {
            title: t("form.ssn.info.1.title", "YOUR DATA IS PROTECTED"),
            description: t(
              "form.ssn.info.1.description",
              "We use industry-best encryption and security technology to keep your personal information safe."
            ),
            imgSrc: BlobPadlock,
          },
        ];

        return <FormValueProps content={adCardContent} vertical />;
      }

      case "terminal-illness-rider": {
        const content = [
          {
            description: t(
              "terminal-illness-rider.value_prop.data.message",
              "This rider is automatically included with a Wysh policy at no additional fee. If you're diagnosed with a terminal illness, you may qualify to receive an early death benefit."
            ),
            imgSrc: BlobTime,
            title: t(
              "terminal-illness-rider.value_prop.data.title",
              "TERMINAL ILLNESS ACCELERATED DEATH BENEFIT RIDER"
            ),
          },
        ];

        return <FormValueProps content={content} />;
      }

      case "sms-transactional": {
        const title = (
          <>
            <Text tag="c4" as="span" className="uppercase">
              {t("sms_transactional.value_prop.title", "SMS Account updates")}
            </Text>{" "}
            <Text
              tag="p4"
              as="span"
              text={t("sms_transactional.value_prop.required_text", "(required)")}
            />
          </>
        );
        const description = t(
          "sms_transactional.value_prop.description",
          "Receive transactional and security alerts"
        );
        const content = {
          title,
          description,
          icon: { src: "Check" },
        };

        return <FormDecorator content={content} />;
      }

      case "thin_divider": {
        return <Divider style={{ margin: "8px 0" }} />;
      }

      case "resume-application-image": {
        return (
          <div className="dynamic-form-item__decoration--illustration">
            <img
              className="dynamic-form-item__decoration--image"
              alt="Person drawing on a map of the USA"
              src={ResumeApplicationImg}
            />
          </div>
        );
      }

      case "underwriting-next-steps": {
        return <UnderwritingSteps />;
      }

      case "underwriting-request-timeout-image": {
        return (
          <TransitionImage
            style={{ padding: "0 20px", maxWidth: "100%", margin: "0 auto" }}
            className="dynamic-form-item__decoration--image"
            alt="Underwriting Request Timeout Hero"
            transition={{ delay: 0.25, ease: "easeInOut" }}
            src={UnderwritingIntroImage}
          />
        );
      }

      default:
        return null;
    }
  }, [slug, t]);

  return getDecoratorBySlug;
};
